<template>
  <div class="notification-panel">
    <Tooltip content="通知" placement="bottom">
      <Badge :count="unreadCount" :overflow-count="99">
        <div
          class="notification-panel__trigger"
          role="button"
          tabindex="0"
          aria-label="通知"
          @click.stop="openPanel"
          @keydown.enter.prevent="openPanel"
          @keydown.space.prevent="openPanel"
        >
          <Icon type="md-notifications" :size="22" />
        </div>
      </Badge>
    </Tooltip>

    <div v-show="visible" class="notification-panel__mask" @click="closePanel" />
    <div v-show="visible" class="notification-panel__drawer">
      <div class="notification-panel__header">
        <span class="notification-panel__header-title">
          通知{{ list.length ? `（${list.length}）` : '' }}
        </span>
        <a class="notification-panel__close" @click="closePanel">
          <Icon type="ios-close" :size="28" />
        </a>
      </div>
      <div class="notification-panel__toolbar">
        <Button
          class="notification-panel__toolbar-btn"
          size="small"
          :disabled="!selectedIds.length || actionLoading"
          @click="handleBatchRead"
        >
          标为已读
        </Button>
        <Button
          class="notification-panel__toolbar-btn"
          size="small"
          :disabled="!list.length || actionLoading"
          @click="confirmReadAll"
        >
          全部已读
        </Button>
      </div>
      <div class="notification-panel__content">
        <div v-if="loading" class="notification-panel__empty">加载中…</div>
        <div v-else-if="!list.length" class="notification-panel__empty">暂无未读通知</div>
        <div v-else class="notification-panel__list">
          <div
            v-for="item in list"
            :key="item.id"
            class="notification-panel__item"
            @click="handleRowClick(item)"
          >
            <div class="notification-panel__checkbox" @click.stop>
              <Checkbox
                :value="isSelected(item.id)"
                @on-change="checked => toggleSelect(item.id, checked)"
              />
            </div>
            <div class="notification-panel__body">
              <div class="notification-panel__title" :title="item.title">{{ item.title }}</div>
              <div v-if="item.summary" class="notification-panel__summary" :title="item.summary">
                {{ item.summary }}
              </div>
              <div v-if="item.created_at" class="notification-panel__time">{{ item.created_at }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import './notification-panel.less'
  import { mapActions } from 'vuex'
  import {
    apiNotificationList,
    apiNotificationRead,
    apiNotificationReadBatch,
    apiNotificationReadAll,
  } from '@/api/notification'
  import { buildNotificationRoute } from '@/libs/notification-jump'

  export default {
    name: 'NotificationPanel',
    props: {
      unreadCount: {
        type: Number,
        default: 0,
      },
    },
    data() {
      return {
        visible: false,
        loading: false,
        actionLoading: false,
        list: [],
        selectedIds: [],
      }
    },
    methods: {
      ...mapActions(['fetchUnreadCount']),
      /**
       * 解析列表接口 data 字段
       * @param {object} res
       * @returns {Array<object>}
       */
      parseListResponse(res) {
        if (!res) {
          return []
        }
        if (Array.isArray(res.data)) {
          return res.data
        }
        if (res.data && Array.isArray(res.data.data)) {
          return res.data.data
        }
        return []
      },
      /**
       * @param {number|string} id
       * @returns {boolean}
       */
      isSelected(id) {
        return this.selectedIds.indexOf(id) >= 0
      },
      /**
       * @param {number|string} id
       * @param {boolean} checked
       */
      toggleSelect(id, checked) {
        const next = this.selectedIds.filter(item => item !== id)
        if (checked) {
          next.push(id)
        }
        this.selectedIds = next
      },
      /** 打开面板并拉列表 */
      openPanel() {
        this.visible = true
        this.fetchList()
      },
      /** 关闭面板 */
      closePanel() {
        this.visible = false
        this.selectedIds = []
      },
      /** 拉取未读通知列表 */
      fetchList() {
        this.loading = true
        return apiNotificationList({ page: 1, per_page: 50 })
          .then(res => {
            this.list = this.parseListResponse(res)
          })
          .catch(() => {
            this.$Message.error('加载通知失败')
          })
          .finally(() => {
            this.loading = false
          })
      },
      /** 刷新未读数 */
      refreshUnreadCount() {
        return this.fetchUnreadCount()
      },
      /** 批量已读 */
      handleBatchRead() {
        if (!this.selectedIds.length) {
          return
        }
        this.actionLoading = true
        apiNotificationReadBatch(this.selectedIds)
          .then(() => {
            this.selectedIds = []
            return Promise.all([this.fetchList(), this.refreshUnreadCount()])
          })
          .finally(() => {
            this.actionLoading = false
          })
      },
      /** 全部已读确认 */
      confirmReadAll() {
        this.$Modal.confirm({
          title: '全部已读',
          content: '确定将所有未读通知标记为已读吗？',
          onOk: () => this.handleReadAll(),
        })
      },
      /** 全部已读 */
      handleReadAll() {
        this.actionLoading = true
        return apiNotificationReadAll()
          .then(() => {
            this.selectedIds = []
            return Promise.all([this.fetchList(), this.refreshUnreadCount()])
          })
          .finally(() => {
            this.actionLoading = false
          })
      },
      /**
       * 点击行：已读并跳转
       * @param {object} item
       */
      handleRowClick(item) {
        if (this.actionLoading) {
          return
        }
        this.actionLoading = true
        apiNotificationRead(item.id)
          .then(() => {
            const route = buildNotificationRoute(item)
            this.closePanel()
            return this.refreshUnreadCount().then(() => {
              this.$router.push(route)
            })
          })
          .finally(() => {
            this.actionLoading = false
          })
      },
    },
  }
</script>
